import { Box, Collapse, Divider, List, Stack, Tooltip, Typography } from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import React, { useContext, useEffect, useMemo, useState } from "react";

import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import { IconButtonOutlined } from "../v2/utils/IconButtonOutlined";
import CssBaseline from "@mui/material/CssBaseline";
import { makeStyles } from "@mui/styles";
import { ProfileAvatar } from "../v2/utils/ProfileAvatar";
import { ButtonZoom } from "../v2/utils/ButtonZoom";
import { InsightsSelectorDropdown } from "./insights/InsightsSelectorDropdown";
import { useHistory, useLocation } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import {
  DbUser,
  InsightsCtx,
  InsightsMinibarDropdown,
  UniversalModalCtx,
  UserCtx,
} from "../../interfaces";
import { InsightsContext } from "../../context/InsightsContext";
import { Role } from "../../enums";
import { deleteObserver, HandleDataLayer } from "../../Helpers";
import { UniversalModalContext } from "../../context/UniversalModalContext";

const drawerWidth = 325;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: `131px`,
  [theme.breakpoints.up("sm")]: {
    width: `132px`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    "& .MuiPaper-root": {
      overflowY: "visible !important",
      overflowX: "visible !important",
      maxHeight: "100vh",
    },
    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
    }),
  })
);

const useStyles = makeStyles((theme) => ({
  logo: {
    color: theme.palette.text.primary,
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: 0.18,
    marginRight: "auto",
    marginTop: 32,
    marginLeft: 32,
    marginBottom: 8,
  },
  faIcon: {
    marginRight: "8px",
  },
  faIconMarginLeft: {
    marginLeft: "auto",
  },
  summaryText: {
    color: "#5D5D5D",
    fontSize: "16px",
    letterSpacing: "0px",
  },
}));

interface MiniSideDrawerProps {
  children?: React.ReactNode;
}

interface SideBarItemProps {
  label: string;
  icon: string;
  path: string;
  subItems?: SideBarItemProps[];
}

interface SideBarItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  subItems: SideBarItemProps[];
}

const CLASS_INSIGHTS_SUB_ITEMS: { [key: string]: SideBarItemProps[] } = {
  insights: [
    {
      label: "Summary",
      icon: "fa-chart-line",
      path: "/insights/class/:class_id/summary",
    },
    {
      label: "Detailed view",
      icon: "fa-chart-line",
      path: "/insights/class/:class_id/details",
    },
    {
      label: "Hints/Errors",
      icon: "fa-chart-line",
      path: "/insights/class/:class_id/hints-and-errors",
    },
  ],
};

const CLASS_SIDEBAR_ITEMS: SideBarItem[] = [
  {
    id: "insights",
    label: "Insights",
    icon: "fa-chart-line",
    subItems: CLASS_INSIGHTS_SUB_ITEMS.insights,
  },
  {
    id: "students",
    label: "Students",
    icon: "fa-screen-users",
    path: "/insights/class/:class_id/students",
    subItems: [],
  },
  {
    id: "settings",
    label: "Class Settings",
    icon: "fa-cog",
    path: "/insights/class/:class_id/settings",
    subItems: [],
  },
];

const STUDENT_SIDEBAR_ITEMS: SideBarItem[] = [
  {
    id: "insights",
    label: "Insights",
    icon: "fa-chart-line",
    path: "/insights/class/:class_id/student/:student_id/summary",
    subItems: [],
  },
  {
    id: "students",
    label: "Student Info",
    icon: "fa-screen-users",
    path: "/insights/class/:class_id/student/:student_id/info",
    subItems: [],
  },
];

interface NavigationItemProps {
  open: boolean;
  title: string;
  subItems: SideBarItemProps[];
  icon: string;
  path?: string;
  handleClick: (pathTo: string) => void;
  checkIsSelected: (pathTo: string) => boolean;
  component: string; // added for data layer requirement
}

const NavigationItem = (props: NavigationItemProps) => {
  const classes = useStyles();
  const theme = useTheme();

  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const { subItems = [], handleClick, checkIsSelected, component } = props;
  const selected = props.path && checkIsSelected(props.path);

  const handleSubMenuItemClick = (pathTo: string) => {
    setSubMenuOpen(false);
    handleClick(pathTo);
  };

  return (
    <>
      <Tooltip title={props.open ? "" : props.title} arrow={true} placement={"right"}>
        <Box
          tabIndex={0}
          sx={{
            // width: "278px",
            minWidth: `${props.open ? "278px" : "0px"}`,
            ...(!props.open && {
              justifyContent: "center",
              marginRight: "auto",
            }),
            height: "72px",
            display: "flex",
            flexDirection: "row",
            gap: "12px",
            alignItems: "center",
            cursor: "pointer",
            padding: "24px 16px",
            borderRadius: " 8px",

            "&:hover": {
              background: "#F3F3F3 0% 0% no-repeat padding-box",
              "& .fa-regular": {
                display: "none",
              },
              "& .fa-solid": {
                display: "inline-block",
              },
            },
            "&:focus-visible": {
              border: "2px solid black",
              "& .fa-regular": {
                display: "none",
              },
              "& .fa-solid": {
                display: "inline-block",
              },
            },
            "& .fa-regular": {
              display: "inline-block",
            },
            "& .fa-solid": {
              display: "none",
            },
            ...(selected && {
              backgroundColor: "#0000000A",
              color: theme.palette.primary.main,
              "& .fa-solid": {
                display: "inline-block",
              },
              "& .fa-regular": {
                display: "none",
              },
            }),
          }}
          onClick={() => {
            if (props.path) {
              HandleDataLayer(
                "link",
                "click",
                "internal link",
                "navbar",
                "sidebar",
                props.title,
                "onclick",
                component
              );
              handleClick(props.path);
            } else if (!props.open && props.subItems) {
              HandleDataLayer(
                "link",
                "click",
                "internal link",
                "navbar",
                "sidebar",
                props.title,
                "onclick",
                component
              );
              handleClick(props.subItems[0].path);
            } else if (props.subItems?.length === 0) {
              // this is triggered when student insights is loaded
              HandleDataLayer(
                "link",
                "click",
                "internal link",
                "navbar",
                "sidebar",
                props.title,
                "onclick",
                component
              );
              handleClick("/insights/:insights_domain/:id/summary");
            } else if (props.subItems?.length > 0) {
              if (subMenuOpen) {
                HandleDataLayer(
                  "collapse",
                  "close",
                  "click",
                  "navbar",
                  "sidebar",
                  props.title,
                  "onclick",
                  component
                );
              } else {
                HandleDataLayer(
                  "collapse",
                  "open",
                  "click",
                  "navbar",
                  "sidebar",
                  props.title,
                  "onclick",
                  component
                );
              }
              setSubMenuOpen(!subMenuOpen);
            }
          }}>
          <i className={`fa-regular ${props.icon}`}></i>
          <i className={`fa-solid ${props.icon}`}></i>
          <Typography
            variant="h2"
            component="span"
            sx={{
              fontSize: "1.3125rem",
              display: props.open ? "block" : "none",
              fontFamily: "Roboto",
            }}>
            {props.title}
          </Typography>
          {props.open && subItems.length > 0 && (
            <i
              className={`far ${subMenuOpen ? "fa-chevron-up" : "fa-chevron-down"} ${
                classes.faIconMarginLeft
              }`}></i>
          )}
        </Box>
      </Tooltip>
      <Collapse
        sx={{ display: props.open ? "block" : "none" }}
        in={subMenuOpen}
        timeout="auto"
        unmountOnExit>
        <List component="div" disablePadding>
          {subItems.map((item, index) => {
            const subSelected = item.path && checkIsSelected(item.path);
            // if (!subMenuOpen && subSelected) {
            //   setSubMenuOpen(true);
            // }

            return (
              <Box
                tabIndex={0}
                key={`sidebar-subitem-${index}-${item.label}`}
                sx={{
                  ml: 8,
                  width: "231px",
                  height: "56px",
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "24px 16px",
                  borderRadius: " 8px",
                  "&:hover": {
                    background: "#F3F3F3 0% 0% no-repeat padding-box",
                  },
                  ...(subSelected && {
                    backgroundColor: "#0000000A",
                    color: theme.palette.primary.main,
                  }),
                }}
                onClick={() => {
                  HandleDataLayer(
                    "link",
                    "click",
                    "internal link",
                    "navbar",
                    "sidebar",
                    item.label,
                    "onclick",
                    props.title
                  );
                  if (item.path) handleSubMenuItemClick(item.path);
                }}>
                <Typography
                  variant="h2"
                  component="span"
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "1.3125rem",
                    display: props.open ? "block" : "none",
                  }}>
                  {item.label}
                </Typography>
              </Box>
            );
          })}
        </List>
      </Collapse>
    </>
  );
};

export const MiniSideDrawer = (props: MiniSideDrawerProps) => {
  const userCtx = useContext(UserContext) as UserCtx;
  const insightsCtx = useContext(InsightsContext) as InsightsCtx;
  const { classId, studentId, insightsView } = insightsCtx;

  const universalModalCtx = useContext(UniversalModalContext) as UniversalModalCtx;
  const currentUser: DbUser = userCtx.dbUser;
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();
  const [open, setOpen] = React.useState(true);

  const [minibarDropdownItems, setMinibarDropdownItems] = React.useState(
    [] as InsightsMinibarDropdown[]
  );
  const [selectedMinibarDropdownIndex, setSelectedMinibarDropdownIndex] = React.useState<
    number | null
  >(null);

  const [classSelectorMenuOpen, setClassSelectorMenuOpen] = React.useState(false);

  const selectedDropdownItem = useMemo(() => {
    if (selectedMinibarDropdownIndex === null || minibarDropdownItems.length < 1)
      return null;
    return minibarDropdownItems[selectedMinibarDropdownIndex];
  }, [minibarDropdownItems, selectedMinibarDropdownIndex]);

  const sideBarItems = useMemo<SideBarItem[]>(() => {
    let items: SideBarItem[] = [];

    if (insightsView === "class") {
      items = [...CLASS_SIDEBAR_ITEMS];

      if (currentUser.role === Role.super) {
        items = [
          ...items,
          {
            id: "assignments",
            label: "Assignments",
            icon: "fa-file",
            path: "/insights/class/:class_id/assignments",
            subItems: [],
          },
        ];
      }
    } else if (insightsView === "student") {
      items = [...STUDENT_SIDEBAR_ITEMS];
    }

    const metadata = insightsCtx.classInfo?.course_metadata;

    // Remove entire insights item
    if (metadata?.exclude_from_insights) {
      items = items.filter((item) => item.id !== "insights");
    }

    // Remove specific sub item
    if (metadata?.insights_page?.disable_feedback_data) {
      items = items.map((item) => {
        if (item.id === "insights" && item.subItems) {
          return {
            ...item,
            subItems: item.subItems.filter((subItem) => subItem.label !== "Hints/Errors"),
          };
        }
        return item;
      });
    }

    return items;
  }, [insightsView, currentUser.role, insightsCtx.classInfo]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  useEffect(() => {
    if (!open) {
      setClassSelectorMenuOpen(false);
    }
  }, [open]);

  const substitutePathParams = (pathTo: string) => {
    if (!classId || !insightsView) {
      console.error("classId or insightsView is null or undefined");
      return undefined;
    }
    let path = pathTo.replace(":class_id", `${classId}`);
    if (studentId) {
      path = path.replace(":student_id", studentId);
    }
    return path;
  };

  const handleClickItem = (pathTo: string) => {
    const path = substitutePathParams(pathTo);
    if (path) {
      history.push(path);
    }
  };

  const checkIsSelected = (pathTo: string) => {
    const path = substitutePathParams(pathTo);
    return location.pathname === path;
  };

  useEffect(() => {
    if (!userCtx.dbUser) return;
    if (!studentId && !classId && !insightsView) return;
    if (!insightsCtx.classInfo) return;

    let currentClass = insightsCtx.classInfo;
    let dropDownItems = [] as InsightsMinibarDropdown[];

    let userStudents = [
      ...(insightsCtx.studentsActive ?? []),
      ...(insightsCtx.studentsArchived ?? []),
    ];

    let userClasses = [
      ...(insightsCtx.classesActive ?? []),
      ...(insightsCtx.observingClassesActive ?? []),
      ...(insightsCtx.classesArchived ?? []),
      ...(insightsCtx.observingClassesArchived ?? []),
    ];

    // for setting the selected class/student
    if (insightsView === "class") {
      const item: InsightsMinibarDropdown = {
        id: `${currentClass.id}`,
        class_id: currentClass.id,
        label:
          currentClass.settings?.nickname ??
          `${currentClass?.course_name} ${currentClass?.period_block}  ${
            currentClass?.start_date &&
            new Date(currentClass.start_date).toLocaleDateString("en-US", {
              timeZone: "UTC",
            })
          }`,
        insights_domain_id: currentClass.id.toString(),
        insights_domain: "class",
        metadata:
          userCtx.dbUser.role !== Role.teacher
            ? {
                teacherName: currentClass.teacher_name,
                schoolName: currentClass.school_name,
                course: currentClass.course_name,
              }
            : undefined,
      };
      dropDownItems.push(item);
    } else if (insightsView === "student" && insightsCtx.userInfo) {
      if (userStudents.map((item) => item.id).includes(insightsCtx.userInfo.id)) {
        const currentStudent = insightsCtx.userInfo;
        const studentSchool =
          currentStudent.user_school && currentStudent.user_school.length
            ? currentStudent.user_school[0].school
            : undefined;
        const studentClass = currentStudent.class_student.find(
          (item) => item.class_id === classId
        )?.class;
        const studentClassTeacher = studentClass?.user as DbUser | undefined;
        const item: InsightsMinibarDropdown = {
          id: `${currentStudent.id}-${studentClass?.id}`,
          class_id: studentClass?.id,
          student_id: currentStudent.id,
          label: `${currentStudent.full_name} (${studentClass?.subject} ${studentClass?.period_block})`,
          insights_domain_id: currentStudent.id.toString(),
          insights_domain: "student",
          metadata:
            userCtx.dbUser.role !== Role.teacher
              ? {
                  teacherName: studentClassTeacher?.full_name,
                  schoolName: studentSchool?.name,
                  course: studentClass?.course?.name,
                }
              : undefined,
        };

        dropDownItems.push(item);
      } else {
      }
    }

    // populate observed and main classes
    if (
      userCtx.dbUser.role === Role.teacher &&
      (insightsCtx.classesActive ||
        insightsCtx.observingClassesActive ||
        insightsCtx.classesArchived ||
        insightsCtx.observingClassesArchived ||
        insightsCtx.studentsActive ||
        insightsCtx.studentsArchived)
    ) {
      for (let userClass of userClasses) {
        const item: InsightsMinibarDropdown = {
          id: `${userClass.id}`,
          class_id: userClass.id,
          label:
            userClass.settings?.nickname ??
            `${userClass?.course_name} ${userClass?.period_block}  ${
              userClass?.start_date &&
              new Date(userClass.start_date).toLocaleDateString("en-US", {
                timeZone: "UTC",
              })
            }`,
          insights_domain_id: userClass.id.toString(),
          insights_domain: "class",
          metadata:
            insightsCtx.observingClassesActive?.includes(userClass) ||
            insightsCtx.observingClassesArchived?.includes(userClass)
              ? {
                  observingType: "OBSERVER_CLASS",
                  teacherName: userClass.teacher_name,
                  schoolName: userClass.school_name,
                }
              : undefined,
        };
        dropDownItems.push(item);
      }

      // populate observed students
      for (let userStudent of userStudents) {
        if (!userStudent.observer_details || !userStudent.observer_details?.class_id)
          break;
        const studentSchool =
          userStudent.user_school && userStudent.user_school.length
            ? userStudent.user_school[0].school
            : undefined;
        const studentClass = userStudent.observer_details?.class;
        const studentClassTeacher = studentClass?.user as DbUser | undefined;

        const item: InsightsMinibarDropdown = {
          id: `${userStudent.id}-${userStudent.observer_details?.class_id}`,
          class_id: userStudent.observer_details?.class_id,
          student_id: userStudent.id,
          label: `${userStudent.full_name} (${userStudent.observer_details?.class?.subject} ${userStudent.observer_details?.class?.period_block})`,
          insights_domain_id: userStudent.id.toString(),
          insights_domain: "student",
          metadata:
            insightsCtx.studentsActive?.includes(userStudent) ||
            insightsCtx.studentsArchived?.includes(userStudent)
              ? {
                  observingType: "OBSERVER_STUDENT",
                  teacherName: studentClassTeacher?.full_name,
                  schoolName: studentSchool?.name,
                  course: studentClass?.course?.name,
                }
              : undefined,
        };
        dropDownItems.push(item);
      }
    }

    const dropdownIds = dropDownItems.map(({ id }) => id);
    let uniquedropdownItems = dropDownItems.filter(
      ({ id }, index) => !dropdownIds.includes(id, index + 1)
    );
    uniquedropdownItems.forEach((item, index) => {
      console.log(item);
      if (insightsView === "class" && item.id === `${classId}`) {
        setSelectedMinibarDropdownIndex(index);
      } else if (insightsView === "student" && item.student_id === `${studentId}`) {
        setSelectedMinibarDropdownIndex(index);
      }
    });

    if (
      insightsView === "student" &&
      insightsCtx.userInfo?.id &&
      !dropdownIds.includes(insightsCtx.userInfo?.id)
    ) {
      const parentClass = uniquedropdownItems.find((item) => item.id === `${classId}`);
      if (parentClass) {
        const parentClassIndex = uniquedropdownItems.indexOf(parentClass);
        setSelectedMinibarDropdownIndex(parentClassIndex);
      }
    }

    setMinibarDropdownItems(uniquedropdownItems);
  }, [
    insightsView,
    userCtx,
    insightsCtx,
    classId,
    studentId,
    insightsCtx.classInfo,
    insightsCtx.userInfo,
    insightsCtx.classesActive,
    insightsCtx.observingClassesActive,
    insightsCtx.classesArchived,
    insightsCtx.observingClassesArchived,
    insightsCtx.studentsActive,
    insightsCtx.studentsArchived,
  ]);

  const handleHomeButtonClick = () => {
    history.push({
      pathname: "/class-overview",
      // commented out per QA fix
      // search: `?teacherId=${insightsCtx.classInfo?.teacher_id}&schoolId=${insightsCtx.classInfo?.school_id}`,
    });
    HandleDataLayer(
      "link",
      "click",
      "internal link",
      "navbar",
      "sidebar",
      "home",
      "onclick"
    );
  };

  const handleRemoveObserverAccess = async (
    currentAccessType: string,
    observerId: string
  ) => {
    universalModalCtx.setLoaderDialogText("Navigating back to home..");
    universalModalCtx.setLoaderDialogOpen(true);
    try {
      await deleteObserver({
        type: currentAccessType,
        grantToId: observerId,
        ...(insightsView === "class" ? { classId: classId } : { studentId: studentId }),
      });
      universalModalCtx.setLoaderDialogOpen(false);
      handleHomeButtonClick();
    } catch (e) {
      console.log(e);
    }
  };

  const collapseButton = (
    <Box
      sx={{
        position: "absolute",
        right: "-16px",
        top: "24px",
        zIndex: 1300,
      }}>
      <IconButtonOutlined
        size="small"
        onClick={() => {
          if (open) {
            HandleDataLayer(
              "collapse",
              "close",
              "click",
              "navbar",
              "sidebar",
              "collapse sidebar button",
              "onclick"
            );
          } else {
            HandleDataLayer(
              "collapse",
              "open",
              "click",
              "navbar",
              "sidebar",
              "expand sidebar button",
              "onclick"
            );
          }
          handleDrawerToggle();
        }}
        sx={{
          marginLeft: "auto",
          width: 34,
          height: 34,
          position: "relative",
        }}
        tooltipLabel={open ? "Collapse sidebar" : "Expand sidebar"}
        tooltipPlacement="right">
        {open ? (
          <ChevronLeftIcon style={{ width: "24px", height: "24px" }} />
        ) : (
          <ChevronRightIcon style={{ width: "24px", height: "24px" }} />
        )}
      </IconButtonOutlined>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Box
        sx={{
          position: "relative",
          maxHeight: "100vh",
        }}>
        <Drawer variant="permanent" open={open}>
          {collapseButton}
          <Box sx={{ overflowY: "auto", maxHeight: "100vh" }}>
            <DrawerHeader>
              <Typography
                variant="h4"
                component="h1"
                noWrap
                sx={{
                  display: { xs: "none", sm: "flex" },
                }}
                className={classes.logo}>
                Digit
              </Typography>
            </DrawerHeader>
            <Box
              sx={{
                gap: "16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <ProfileAvatar
                hideStar={!open}
                initials={(
                  currentUser.first_name[0] + currentUser.last_name[0]
                ).toUpperCase()}
              />
              <ButtonZoom
                disableZoom={true}
                size={"small"}
                variant="outlined"
                sx={{
                  width: "109px",
                  height: "36px",
                  borderRadius: "8px",
                  marginTop: "16px",
                  fontSize: "16px",
                  ...(!open && {
                    display: "none",
                  }),
                  "&:hover": {
                    transform: "none",
                  },
                }}
                iconPosition="left"
                icon={<i className={`far fa-house ${classes.faIcon}`}></i>}
                iconHover={<i className={`fas fa-house ${classes.faIcon}`}></i>}
                onClick={handleHomeButtonClick}>
                Home
              </ButtonZoom>
              <Tooltip title={"Home"} arrow={true} placement={"right"}>
                <Box
                  tabIndex={0}
                  sx={{
                    ...(!open && {
                      marginLeft: "auto",
                      justifyContent: "center",
                      marginRight: "auto",
                      minWidth: "0px",
                    }),
                    height: "72px",
                    display: !open ? "flex" : "none",
                    flexDirection: "row",
                    gap: "12px",
                    alignItems: "center",
                    cursor: "pointer",
                    padding: "24px 16px",
                    "&:hover": {
                      background: "#F3F3F3 0% 0% no-repeat padding-box",
                      borderRadius: " 8px",
                      "& .fa-regular": {
                        display: "none",
                      },
                      "& .fa-solid": {
                        display: "inline-block",
                      },
                    },
                    "&:focus-visible": {
                      border: "2px solid black",
                      "& .fa-regular": {
                        display: "none",
                      },
                      "& .fa-solid": {
                        display: "inline-block",
                      },
                    },
                    "& .fa-regular": {
                      display: "inline-block",
                    },
                    "& .fa-solid": {
                      display: "none",
                    },
                  }}
                  onClick={handleHomeButtonClick}>
                  <i className={`fa-regular fa-house`}></i>
                  <i className={`fa-solid fa-house`}></i>
                </Box>
              </Tooltip>
              <Box sx={{ padding: "0px 32px", width: "100%" }}>
                <Divider sx={{ width: "100%" }} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  ...(!open && {
                    display: "none",
                  }),
                  minWidth: "278px",
                  width: "100%",
                  padding: "0px 32px",
                  alignItems: "flex-start",
                }}>
                <InsightsSelectorDropdown
                  dropDownMenuOpen={classSelectorMenuOpen}
                  sx={{ width: "100%" }}
                  disableDropdown={userCtx.dbUser.role !== Role.teacher}
                  dropDownItems={minibarDropdownItems}
                  onChange={(item) => {
                    HandleDataLayer(
                      "link",
                      "click",
                      "internal link",
                      "navbar",
                      "side navbar",
                      item.label,
                      "onClick"
                    );
                    if (item.insights_domain === "class") {
                      history.replace(
                        insightsCtx.getClassUrl(Number(item.insights_domain_id))
                      );
                    } else if (item.insights_domain === "student") {
                      if (item.class_id && item.student_id) {
                        const newPath = insightsCtx.getStudentUrl(
                          item.student_id,
                          item.class_id
                        );
                        history.replace(newPath);
                      }
                    }
                  }}
                  selectedItemIndex={selectedMinibarDropdownIndex}
                  selectedItem={selectedDropdownItem}
                />
                {selectedDropdownItem?.metadata && (
                  <Stack flexDirection={"column"} gap={4}>
                    {selectedDropdownItem?.metadata.teacherName && (
                      <Stack
                        sx={{ marginTop: "16px" }}
                        flexDirection="row"
                        alignItems="center"
                        gap={"10px"}>
                        <i className={`far fa-user fa-md ${classes.summaryText}`}></i>
                        <Typography
                          variant="body2"
                          component={"span"}
                          className={classes.summaryText}>
                          {selectedDropdownItem?.metadata.teacherName}
                        </Typography>
                      </Stack>
                    )}
                    {selectedDropdownItem?.metadata.schoolName && (
                      <Stack flexDirection="row" alignItems="center" gap={"8px"}>
                        <i className={`far fa-school fa-md ${classes.summaryText}`}></i>
                        <Typography
                          variant="body2"
                          component={"span"}
                          className={classes.summaryText}>
                          {selectedDropdownItem?.metadata.schoolName}
                        </Typography>
                      </Stack>
                    )}
                    {selectedDropdownItem?.metadata.course && (
                      <Stack flexDirection="row" alignItems="center" gap={"14px"}>
                        <i className={`far fa-book fa-md ${classes.summaryText}`}></i>
                        <Typography
                          variant="body2"
                          component={"span"}
                          className={classes.summaryText}>
                          {selectedDropdownItem?.metadata.course}
                        </Typography>
                      </Stack>
                    )}
                    {selectedDropdownItem?.metadata.observingType && (
                      <>
                        <Stack flexDirection="row" alignItems="center" gap={"10px"}>
                          <i className={`far fa-eye fa-md ${classes.summaryText}`}></i>
                          <Typography
                            variant="body2"
                            component={"span"}
                            className={classes.summaryText}>
                            {selectedDropdownItem?.metadata.observingType ===
                            "OBSERVER_STUDENT"
                              ? "You're observing this student"
                              : "You're observing this class"}
                          </Typography>
                        </Stack>
                        <Stack flexDirection="row" alignItems="center">
                          <Typography
                            onClick={() => {
                              HandleDataLayer(
                                "modal",
                                "open",
                                "click",
                                "navbar",
                                "sidebar",
                                "stop observing",
                                "onclick",
                                selectedDropdownItem?.label
                              );
                              handleRemoveObserverAccess(
                                selectedDropdownItem?.metadata?.observingType!,
                                currentUser.id
                              );
                            }}
                            variant="body2"
                            component={"span"}
                            sx={{
                              color: "#5D5D5D",
                              fontSize: "16px",
                              letterSpacing: "0px",
                              textDecoration: "underline",
                              marginLeft: "26px",
                              cursor: "pointer",
                            }}>
                            Stop observing
                          </Typography>
                        </Stack>
                      </>
                    )}
                  </Stack>
                )}
              </Box>
              <Tooltip title={"Your classes"} arrow={true} placement={"right"}>
                <Box
                  tabIndex={0}
                  sx={{
                    ...(!open && {
                      marginLeft: "auto",
                      justifyContent: "center",
                      marginRight: "auto",
                      minWidth: "0px",
                    }),
                    height: "72px",
                    display: !open ? "flex" : "none",
                    flexDirection: "row",
                    gap: "12px",
                    alignItems: "center",
                    cursor: "pointer",
                    padding: "24px 16px",
                    "&:hover": {
                      background: "#F3F3F3 0% 0% no-repeat padding-box",
                      borderRadius: " 8px",
                      "& .fa-regular": {
                        display: "none",
                      },
                      "& .fa-solid": {
                        display: "inline-block",
                      },
                    },
                    "&:focus-visible": {
                      border: "2px solid black",
                      "& .fa-regular": {
                        display: "none",
                      },
                      "& .fa-solid": {
                        display: "inline-block",
                      },
                    },
                    "& .fa-regular": {
                      display: "inline-block",
                    },
                    "& .fa-solid": {
                      display: "none",
                    },
                  }}
                  onClick={() => {
                    handleDrawerToggle();
                    setClassSelectorMenuOpen(true);
                  }}>
                  <i className={`fa-regular fa-chalkboard-user`}></i>
                  <i className={`fa-solid fa-chalkboard-user`}></i>
                </Box>
              </Tooltip>
              <Box sx={{ padding: "0px 32px", width: "100%" }}>
                <Divider sx={{ width: "100%" }} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  height: "100%",
                  // overflowY: "auto",
                }}>
                {sideBarItems.map((item, index) => {
                  return (
                    <NavigationItem
                      component={selectedDropdownItem?.label ?? ""}
                      open={open}
                      key={`sidebar-item-${index}`}
                      path={item.path}
                      title={item.label}
                      icon={item.icon}
                      subItems={item.subItems}
                      handleClick={handleClickItem}
                      checkIsSelected={checkIsSelected}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          position: "relative",
          maxHeight: "100%",
          overflowX: "visible",
          overflowY: "auto",
        }}>
        {props.children}
      </Box>
    </Box>
  );
};
